import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'

import runtimeBudgetApi from 'domain/runtimeBudget/api'
import { RuntimeBudgetErrorCode } from 'domain/runtimeBudget/schemas'
import { resolveTaskDisplayBudget } from 'domain/runtimeBudget/mappers'
import campaignStatusApi from 'domain/campaignStatus/api'
import { parseDateYYYYMMDD } from 'utils/helpers'
import { useImmerReducerAsync } from 'hooks/useReducerAsync'
import './CampaignConfigProvider.types.js'

/**
 * @type {React.Context<CampaignConfigStateType>}
 */
export const CampaignConfigContext = createContext()

/**
 * @type {React.Context<CampaignConfigDispatchType>}
 */
export const CampaignConfigDispatchContext = createContext()

export const actions = Object.freeze({
  fetchCampaignAsync: 'FETCH_CAMPAIGN_ASYNC',
  fetchTaskAsync: 'FETCH_TASK_ASYNC',
  fetchCampaignStatusAsync: 'FETCH_CAMPAIGN_STATUS_ASYNC',
  setEditMode: 'SET_EDIT_MODE',
  setForm: 'SET_FORM',
  setLoading: 'SET_LOADING',
  setError: 'SET_ERROR',
  setCampaignAndTask: 'SET_CAMPAIGN_AND_TASK',
  setTask: 'SET_TASK',
  setCampaignStatus: 'SET_CAMPAIGN_STATUS',
  setDailyBudgetPreview: 'SET_DAILY_BUDGET_PREVIEW',
  saveAsync: 'SAVE_ASYNC',
})

/**
 * @type {CampaignConfigStateType}
 */
export const initialState = {
  loading: false,
  error: null, // { message, code, status } or null
  campaign: {},
  task: null,
  form: {
    total_budget: 0,
    start_date: null,
    end_date: null,
  },
  editMode: false,
  dailyBudgetPreview: null,
  // Per-platform status (domain/campaignStatus's mapped shape, {google, meta}
  // each with hasPaused/hasActive/hasEnded/published) — only Google
  // reactivates a paused campaign on a budget/runtime save
  // (GOOGLE_REACTIVATES_PAUSED_CAMPAIGN_ON_BUDGET_UPDATE), so this replaces
  // the collapsed v3 event.campaign_status the paused-warning used to read.
  campaignStatus: null,
}

export const reducer = (draft, action) => {
  switch (action.type) {
    case actions.setLoading:
      draft.loading = action.payload
      break
    case actions.setError: {
      // Accepts error object or string, handles axios errors
      let err = action.payload
      let errorSource = err && err.errorSource ? err.errorSource : undefined
      if (typeof err === 'string') {
        err = { message: err, code: null, status: null }
      }
      // Axios error: extract from response
      if (err && err.response) {
        const { status, data } = err.response
        err = {
          ...err,
          status: status || err.status,
          code: (data && (data.code || data.error_code)) || err.code,
          message: (data && (data.message || data.error_message)) || err.message,
          data: data?.data,
          raw: err,
        }
      }
      draft.error = {
        message: err.message || err.error_message || 'Unknown error',
        code: err.code || err.error_code || null,
        status: err.status || err.statusCode || null,
        raw: err,
        ...(err.data ? { data: err.data } : {}),
        ...(errorSource ? { errorSource } : {}),
      }
      draft.loading = false
      break
    }
    case actions.setCampaignAndTask: {
      const { campaign, task } = action.payload
      // Convert date strings to Date objects if present
      const campaignStartDate =
        typeof campaign.start_date === 'string'
          ? parseDateYYYYMMDD(campaign.start_date)
          : campaign.start_date || null
      const campaignEndDate =
        typeof campaign.end_date === 'string'
          ? parseDateYYYYMMDD(campaign.end_date)
          : campaign.end_date || null
      const campaignStatus = campaign.status || 'found'
      draft.campaign = { ...campaign, start_date: campaignStartDate, end_date: campaignEndDate, status: campaignStatus }
      draft.form = {
        ...draft.form,
        total_budget: campaign.total_budget,
        // start_date remains as is (empty unless user changes)
        end_date: campaignEndDate,
      }
      if (task?.args) {
        const taskStartDate =
          typeof task.args.start_date === 'string'
            ? parseDateYYYYMMDD(task.args.start_date)
            : task.args.start_date || null
        const taskEndDate =
          typeof task.args.end_date === 'string'
            ? parseDateYYYYMMDD(task.args.end_date)
            : task.args.end_date || null
        draft.task = {
          ...task,
          args: { start_date: taskStartDate, end_date: taskEndDate, ...task.args },
        }
      }
      draft.loading = false
      draft.error = null
      break
    }
    case actions.setTask: {
      draft.task = action.payload
      draft.loading = false
      break
    }
    case actions.setCampaignStatus: {
      draft.campaignStatus = action.payload
      break
    }
    case actions.setForm: {
      // If start_date or end_date are being set, convert to Date objects
      const nextForm = { ...draft.form, ...action.payload }
      if (nextForm.start_date && typeof nextForm.start_date === 'string') {
        nextForm.start_date = parseDateYYYYMMDD(nextForm.start_date)
      }
      if (nextForm.end_date && typeof nextForm.end_date === 'string') {
        nextForm.end_date = parseDateYYYYMMDD(nextForm.end_date)
      }
      draft.form = nextForm
      break
    }
    case actions.setEditMode:
      draft.editMode = action.payload
      break
    case actions.setDailyBudgetPreview:
      draft.dailyBudgetPreview = action.payload
      draft.loading = false
      break
    default:
      // do nothing
      break
  }
}

function useConfigPolling(shouldPoll, intervalMs, callback) {
  const intervalRef = useRef(null)
  const savedCallback = useRef(callback)

  // keep latest callback without resetting interval
  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  useEffect(() => {
    if (!shouldPoll) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      return
    }

    if (intervalRef.current) return

    intervalRef.current = setInterval(() => {
      savedCallback.current()
    }, intervalMs)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [shouldPoll, intervalMs])
}

export const asyncActionHandlers = {
  [actions.fetchCampaignAsync]:
    ({ dispatch }) =>
      async (action) => {
        dispatch({ type: actions.setLoading, payload: true })
        const { eventId } = action.payload
        const { data, error } = await runtimeBudgetApi.fetchAsync(eventId)
        if (error) {
          if (error.code === RuntimeBudgetErrorCode.remoteCampaignNotFound) {
            dispatch({
              type: actions.setCampaignAndTask,
              payload: {
                campaign: {
                  start_date: null,
                  end_date: null,
                  total_budget: null,
                  currency: null,
                  status: 'not_published',
                },
                task: null,
              },
            })
          } else {
            dispatch({ type: actions.setError, payload: error })
          }
          return
        }
        const campaign = {
          start_date: data.startDate,
          end_date: data.endDate,
          total_budget: data.totalBudget,
          min_daily_budget: data.minDailyBudget,
          min_budget: data.minBudget,
          platforms: data.platforms,
          max_budget: data.maxBudget,
          currency: data.currency,
          has_permission_error: data.hasPermissionError,
          permission_error_platform: data.permissionErrorPlatform,
        }
        // {} (no fields) means no task has ever run for this event — treat like null.
        const task = data.taskDetails && Object.keys(data.taskDetails).length
          ? {
            ...data.taskDetails,
            timestamp: data.taskDetails.datetime ? new Date(data.taskDetails.datetime) : null,
            mappedStatus: mapTaskStatus(data.taskDetails),
            args: data.taskDetails.args
              ? {
                budget: resolveTaskDisplayBudget(data.taskDetails.args),
                start_date: data.taskDetails.args.start_date
                  ? parseDateYYYYMMDD(data.taskDetails.args.start_date)
                  : null,
                end_date: data.taskDetails.args.end_date
                  ? parseDateYYYYMMDD(data.taskDetails.args.end_date)
                  : null,
              }
              : null,
          }
          : null
        dispatch({ type: actions.setCampaignAndTask, payload: { campaign, task } })
        if (campaign.has_permission_error) {
          dispatch({ type: actions.setError, payload: { message: 'Permission error' } })
        } else if (data.errorCode) {
          // A 200 with no figures: the panel would otherwise render blank with no
          // explanation. Not a permission problem, so no reconnect prompt.
          dispatch({
            type: actions.setError,
            payload: { message: 'Runtime budget unavailable', code: data.errorCode },
          })
        }
      },
  // Informational only (backs the paused-campaign warning in PreviewModal) —
  // failures here don't block editing, so they're swallowed rather than
  // routed through setError, which the runtime/budget fetch above already
  // uses for load-blocking failures.
  [actions.fetchCampaignStatusAsync]:
    ({ dispatch }) =>
      async (action) => {
        const { eventId } = action.payload
        const { data } = await campaignStatusApi.fetchAsync(eventId)
        if (data) {
          dispatch({ type: actions.setCampaignStatus, payload: data })
        }
      },
}

export default function CampaignConfigProvider({ children, event }) {
  // This provider only ever mounts inside Goals/index.jsx's `runningCampaigns.length`
  // branch, so a marketing campaign is already known to exist — no need for a
  // synchronous v3 event-flag gate before the first fetch.
  const [state, dispatch] = useImmerReducerAsync(reducer, initialState, asyncActionHandlers)

  const fetchCurrentCampaignConfig = useCallback(() => {
    dispatch({ type: actions.fetchCampaignAsync, payload: { eventId: event.id } })
  }, [dispatch, event.id])

  const fetchCurrentCampaignStatus = useCallback(() => {
    dispatch({ type: actions.fetchCampaignStatusAsync, payload: { eventId: event.id } })
  }, [dispatch, event.id])

  const _hasPendingPublishTask = state.campaign.status === 'not_published'
  const _hasPendingTask = state.task?.mappedStatus?.toLowerCase() === 'pending'
  useConfigPolling(
    _hasPendingPublishTask || _hasPendingTask,
    _hasPendingPublishTask ? 30000 : 180000,
    fetchCurrentCampaignConfig,
  )

  useEffect(() => {
    if (event?.id) {
      fetchCurrentCampaignConfig()
      fetchCurrentCampaignStatus()
    }
    // eslint-disable-next-line
  }, [event?.id])

  const hasPendingPublishTask = useCallback(() => {
    return state.campaign.status === 'not_published'
  }, [state.campaign.status])

  const getDailyBudgetPreview = useCallback(
    async (cb) => {
      dispatch({ type: actions.setLoading, payload: true })
      const { total_budget, start_date, end_date } = state.form
      try {
        const { data, error } = await runtimeBudgetApi.previewAsync(event.id, {
          budget: total_budget,
          startDate: start_date,
          endDate: end_date,
        })
        if (error) {
          dispatch({ type: actions.setError, payload: { ...error, errorSource: 'preview' } })
          if (cb) cb({ success: false, error })
          return
        }
        dispatch({ type: actions.setDailyBudgetPreview, payload: data })
        if (cb) cb({ success: true, data })
      } catch (err) {
        console.error('Error fetching daily budget preview:', err)
        dispatch({ type: actions.setError, payload: { ...err, errorSource: 'preview' } })
        if (cb) cb({ success: false, error: err })
      }
    },
    [dispatch, state.form, event.id],
  )

  const postBudgetAndRuntime = useCallback(
    async (cb) => {
      dispatch({ type: actions.setLoading, payload: true })
      const { total_budget, start_date, end_date } = state.form
      const { start_date: campaignStartDate } = state.campaign
      try {
        const { data, error } = await runtimeBudgetApi.updateAsync(event.id, {
          budget: total_budget,
          startDate: start_date,
          endDate: end_date,
        })
        if (error) {
          dispatch({ type: actions.setError, payload: { ...error, errorSource: 'save' } })
          if (cb) cb({ success: false, error })
          return
        }

        const campaign = {
          total_budget,
          end_date,
          start_date: start_date ?? campaignStartDate,
        }
        // taskDetails is null when the IW dispatch itself silently failed
        // server-side — no pollable task in that case (see runtimeBudget's
        // schemas.js comment on updateTaskDetailsSchema).
        const mappedTask = data.taskDetails
          ? { ...data.taskDetails, mappedStatus: mapTaskStatus(data.taskDetails) }
          : null
        const task = mappedTask
          ? {
            ...mappedTask,
            args: {
              budget: total_budget,
              start_date,
              end_date,
            },
          }
          : null
        dispatch({ type: actions.setCampaignAndTask, payload: { campaign, task } })

        if (cb) cb({ success: true, data: mappedTask })
      } catch (err) {
        dispatch({ type: actions.setError, payload: { ...err, errorSource: 'save' } })
        if (cb) cb({ success: false, error: err })
      }
    },
    [dispatch, state.form, state.campaign, event.id],
  )

  const toggleEditMode = () => {
    dispatch({ type: actions.setEditMode, payload: !state.editMode })
  }

  const resetCampaignForm = () => {
    dispatch({
      type: actions.setForm,
      payload: {
        start_date: state.campaign.start_date,
        end_date: state.campaign.end_date,
        total_budget: state.campaign.total_budget,
      },
    })
  }

  const resetDailyBudgetPreview = () => {
    dispatch({ type: actions.setDailyBudgetPreview, payload: null })
  }

  const setCampaignForm = (data) => {
    dispatch({
      type: actions.setForm,
      payload: data,
    })
  }

  const hasPermissionError = () => {
    if (state.campaign.has_permission_error) return true
    if (!state.error) return false
    return (
      state.error.status === 403 ||
      [
        'error.connect.facebook.invalid_token',
        'error.connect.facebook.missing_permissions',
      ].includes(state.error.code)
    )
  }

  // Which platform ('fb'|'google') a permission error came from, so the copy
  // names the right provider. Prefers an explicit signal from the API, then
  // falls back to inferring it for a single-platform campaign.
  const getPermissionErrorPlatform = () => {
    if (state.error?.data?.platform) return state.error.data.platform
    if (state.campaign.permission_error_platform) return state.campaign.permission_error_platform
    const s = state.campaignStatus
    if (s?.google?.published && !s?.meta?.published) return 'google'
    if (s?.meta?.published && !s?.google?.published) return 'fb'
    return null
  }

  const hasPendingTask = () => {
    if (!state.task) return false
    return state.task?.mappedStatus?.toLowerCase() === 'pending'
  }

  return (
    <CampaignConfigContext.Provider value={state}>
      <CampaignConfigDispatchContext.Provider
        value={{
          dispatch,
          fetchCurrentCampaignConfig,
          toggleEditMode,
          resetCampaignForm,
          setCampaignForm,
          getDailyBudgetPreview,
          resetDailyBudgetPreview,
          postBudgetAndRuntime,
          hasPermissionError,
          getPermissionErrorPlatform,
          hasPendingTask,
          hasPendingPublishTask,
        }}
      >
        {children}
      </CampaignConfigDispatchContext.Provider>
    </CampaignConfigContext.Provider>
  )
}

CampaignConfigProvider.propTypes = {
  children: PropTypes.node.isRequired,
  event: PropTypes.object.isRequired,
}

/**
 * @returns {CampaignConfigStateType}
 */
export function useCampaignConfig() {
  const context = useContext(CampaignConfigContext)
  if (!context) {
    throw new Error('useCampaignConfig must be used within a CampaignConfigProvider')
  }
  return context
}

/**
 * @returns {CampaignConfigDispatchType}
 */
export function useCampaignConfigDispatch() {
  const context = useContext(CampaignConfigDispatchContext)
  if (!context) {
    throw new Error('useCampaignConfigDispatch must be used within a CampaignConfigProvider')
  }
  return context
}

// Map celery task status to domain status for UI
export function mapTaskStatus(task) {
  if (!task || !task.status) return null
  const status = task.status.toLowerCase()
  if (
    [
      'pending',
      'started',
      'in_progress',
      'progress',
      'running',
      'queued',
      'received',
      'scheduled',
      'retry',
    ].includes(status)
  ) {
    return 'pending'
  }
  if (['success'].includes(status)) {
    return 'success'
  }
  if (['failure', 'failed', 'revoked'].includes(status)) {
    return 'failure'
  }
  return status
}
